import { commonText, useCommonLanguage } from "../../i18n/commonPresentation";
/**
 * Shared basemap layer for every dashboard geo map.
 *
 * - Raster tiles (`dark`/`light`/`street`/`satellite`): renders the base
 *   `<TileLayer>` plus the optional Esri label overlay, exactly as the
 *   consumers used to inline.
 * - `theme` (vector): lazily loads MapLibre + the leaflet bridge and paints a
 *   theme-matched vector basemap from the QuickBox tile host. MapLibre is
 *   dynamically imported so non-map routes never pull it into their bundle. If
 *   the vector source is unreachable (e.g. the planet extract has not landed
 *   yet) it silently falls back to the raster `dark` basemap.
 */

import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { TileLayer, useMap } from "react-leaflet";
import { useTheme } from "../theme/ThemeProvider";
import { Tooltip } from "../ui/Tooltip";
import { TILE_PROVIDERS, type TileProvider } from "../../lib/map/tileProviders";
import { buildBasemapStyle } from "../../lib/map/protomapsStyle";

/** Credits shown by the compact chip when the vector basemap is active. */
const VECTOR_CREDITS = "© OpenStreetMap contributors · Protomaps";

/** Raster base tiles + the optional transparent label overlay. */
const RasterBasemap = ({ tile }: { tile: TileProvider }) => {
  const config = TILE_PROVIDERS[tile];
  if (!config.url) return null;
  return (
    <>
      <TileLayer
        key={tile}
        url={config.url}
        subdomains={config.subdomains ?? ""}
        maxZoom={config.maxZoom}
        maxNativeZoom={config.maxNativeZoom}
        attribution={config.attribution}
      />
      {config.labelsUrl && (
        <TileLayer
          key={`${tile}-labels`}
          url={config.labelsUrl}
          maxZoom={config.maxZoom}
          maxNativeZoom={config.maxNativeZoom}
          zIndex={2}
        />
      )}
    </>
  );
};

type MaplibreGLLayer = ReturnType<
  (typeof import("@maplibre/maplibre-gl-leaflet"))["default"]
>;

// MapLibre spins up its tile worker from a URL; point it at MapLibre's own
// worker bundle (Vite `?worker&url` asset) so the worker never imports app chunks that
// touch `document` and die at load. Set once, before any MapLibre map exists.
let workerConfigured = false;

/** MapLibre vector basemap, code-split, theme-reactive, raster-fallback. */
const VectorBasemap = () => {
  const map = useMap();
  const { theme } = useTheme();
  const [failed, setFailed] = useState(false);
  const layerRef = useRef<MaplibreGLLayer | null>(null);
  // Always paint with the latest theme even if it changed mid-import.
  const themeRef = useRef(theme);
  themeRef.current = theme;

  useEffect(() => {
    let cancelled = false;

    const removeLayer = () => {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };

    void (async () => {
      const [{ default: maplibreGL }] = await Promise.all([
        import("@maplibre/maplibre-gl-leaflet"),
        import("maplibre-gl/dist/maplibre-gl.css"),
      ]);
      if (cancelled) return;

      if (!workerConfigured) {
        const [{ setWorkerUrl }, { default: workerUrl }] = await Promise.all([
          import("maplibre-gl"),
          import("maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url"),
        ]);
        setWorkerUrl(workerUrl);
        workerConfigured = true;
      }

      const layer = maplibreGL({ style: buildBasemapStyle(themeRef.current) });
      layer.addTo(map);
      layerRef.current = layer;

      // Only fall back when the vector SOURCE never loads (host/tiles
      // unreachable) — not for incidental per-tile or glyph errors after it
      // has come up.
      let sourceLoaded = false;
      const gl = layer.getMaplibreMap();
      gl.on("sourcedata", (e) => {
        if (e.sourceId === "protomaps" && e.isSourceLoaded) sourceLoaded = true;
      });
      gl.on("error", (e) => {
        const sourceId = (e as { sourceId?: string }).sourceId;
        if (sourceId === "protomaps" && !sourceLoaded && !cancelled) {
          removeLayer();
          setFailed(true);
        }
      });
    })();

    return () => {
      cancelled = true;
      removeLayer();
    };
  }, [map]);

  useEffect(() => {
    if (layerRef.current) {
      layerRef.current.getMaplibreMap().setStyle(buildBasemapStyle(theme));
    }
  }, [theme]);

  if (failed) return <RasterBasemap tile={theme === "defaulted" ? "light" : "dark"} />;
  return null;
};

/**
 * Compact themed credits chip — replaces Leaflet's stock attribution box, which
 * crowds small maps and does not match the theme. Portaled into the Leaflet
 * container, pinned bottom-right. The wrapper is content-sized (`w-fit`, only
 * `right`/`bottom` set), so it covers just the ~20px chip and the map still
 * drags everywhere outside it — deliberately NO `pointer-events-none`, which the
 * Tooltip's trigger span would inherit and which silently kills its hover/focus.
 * Credits render in the global Tooltip (never a native `title=`).
 */
const MapCredits = ({ tile }: { tile: TileProvider }) => {
  useCommonLanguage();
  const map = useMap();
  const config = TILE_PROVIDERS[tile];
  const credits = config.kind === "vector" ? VECTOR_CREDITS : config.attribution;
  if (!credits) return null;

  // The wrapper shrinks to the chip (absolute, right/bottom only), so it covers
  // just its own ~20px box — the map stays draggable everywhere else without a
  // `pointer-events-none` that would also swallow the Tooltip's hover listeners.
  return createPortal(
    <div className="absolute bottom-1 right-1 z-[1000] w-fit">
      <Tooltip content={credits} side="top" align="end" maxWidth="80">
        <button
          type="button"
          aria-label={commonText("mapCredits")}
          className="flex h-5 w-5 items-center justify-center rounded-full border border-border bg-card/80 text-[10px] leading-none text-muted-foreground backdrop-blur-sm transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/70"
        >
          <span aria-hidden="true">©</span>
        </button>
      </Tooltip>
    </div>,
    map.getContainer(),
  );
};

export const BasemapLayer = ({ tile }: { tile: TileProvider }) => {
  return (
    <>
      {TILE_PROVIDERS[tile].kind === "vector" ? <VectorBasemap /> : <RasterBasemap tile={tile} />}
      <MapCredits tile={tile} />
    </>
  );
};
